import React, { useMemo } from 'react';
import { useApp } from '../contexts/AppContext';
import { Calculator, TrendingUp, Target } from 'lucide-react';

const formatCurrency = (val) => `$${(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const FIXED_COST_FIELDS = [
  { key: 'rent', label: 'Monthly Rent' },
  { key: 'utilities', label: 'Utilities' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'software', label: 'Software / Subscriptions' },
  { key: 'payroll', label: 'Fixed Payroll' },
  { key: 'managerSalary', label: 'Manager Salary' },
  { key: 'loanPayments', label: 'Loan Payments' },
  { key: 'otherFixed', label: 'Other Fixed Costs' },
];

export default function BreakEvenCalculator() {
  const { breakEvenData, setBreakEvenData } = useApp();

  const update = (key, value) => {
    setBreakEvenData(prev => ({ ...prev, [key]: value }));
  };

  const calculations = useMemo(() => {
    const totalFixed = FIXED_COST_FIELDS.reduce((sum, f) => sum + (Number(breakEvenData[f.key]) || 0), 0);
    const margin = (Number(breakEvenData.avgContributionMargin) || 1) / 100;
    const avgOrder = Number(breakEvenData.avgOrderValue) || 1;

    const breakEvenRevenue = margin > 0 ? totalFixed / margin : 0;
    const ordersPerMonth = avgOrder > 0 ? breakEvenRevenue / avgOrder : 0;
    const dailyRevenue = breakEvenRevenue / 30;
    const ordersPerDay = ordersPerMonth / 30;

    return { totalFixed, breakEvenRevenue, ordersPerMonth, dailyRevenue, ordersPerDay };
  }, [breakEvenData]);

  return (
    <div className="page-enter p-4 md:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 bg-[#E23D28] rounded-full border-2 border-[#2A2A2A] flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(42,42,42,1)]">
          <Calculator className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-4xl sm:text-5xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }} data-testid="breakeven-title">
            Break-Even Calculator
          </h1>
          <p className="text-sm text-[#2A2A2A]/60">Find out how much you need to sell to cover your nut.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-5 gap-6">
        {/* Fixed Costs Input */}
        <div className="md:col-span-3 space-y-6">
          <div className="pizza-card p-6" data-testid="breakeven-fixed-costs">
            <h2 className="text-2xl text-[#2A2A2A] mb-4" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              Monthly Fixed Costs
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {FIXED_COST_FIELDS.map(({ key, label }) => (
                <div key={key}>
                  <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>{label}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2A2A2A]/40 text-sm">$</span>
                    <input
                      type="number" step="0.01" min="0"
                      value={breakEvenData[key]}
                      onChange={e => update(key, e.target.value)}
                      className="pizza-input w-full pl-7"
                      data-testid={`breakeven-${key}-input`}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t-2 border-[#F0E6D2] flex justify-between items-center">
              <span className="text-sm font-medium text-[#2A2A2A]">Total Monthly Fixed Costs</span>
              <span className="text-2xl font-bold text-[#E23D28]" style={{ fontFamily: "'Bebas Neue', cursive" }} data-testid="breakeven-total-fixed">
                {formatCurrency(calculations.totalFixed)}
              </span>
            </div>
          </div>

          <div className="pizza-card p-6" data-testid="breakeven-averages">
            <h2 className="text-2xl text-[#2A2A2A] mb-4" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              Sales Averages
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Average Order Value ($)</label>
                <input type="number" step="0.01" min="0.01"
                  value={breakEvenData.avgOrderValue}
                  onChange={e => update('avgOrderValue', e.target.value)}
                  className="pizza-input w-full" data-testid="breakeven-aov-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Average Contribution Margin %</label>
                <input type="number" step="1" min="1" max="100"
                  value={breakEvenData.avgContributionMargin}
                  onChange={e => update('avgContributionMargin', e.target.value)}
                  className="pizza-input w-full" data-testid="breakeven-margin-input" />
                <p className="text-xs text-[#2A2A2A]/40 mt-1">Revenue minus variable costs (food + labor). Typical: 55-70%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="md:col-span-2 space-y-4">
          <div className="bg-[#2A2A2A] rounded-xl p-6 text-white" data-testid="breakeven-results">
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-5 h-5 text-[#FFC107]" />
              <h2 className="text-2xl" style={{ fontFamily: "'Bebas Neue', cursive" }}>
                Break-Even Point
              </h2>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-white/50 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>Monthly Revenue Needed</p>
                <p className="text-3xl font-bold text-[#FFC107]" style={{ fontFamily: "'Bebas Neue', cursive" }} data-testid="breakeven-monthly-revenue">
                  {formatCurrency(calculations.breakEvenRevenue)}
                </p>
              </div>
              <div>
                <p className="text-xs text-white/50 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>Daily Revenue Needed</p>
                <p className="text-3xl font-bold text-white" style={{ fontFamily: "'Bebas Neue', cursive" }} data-testid="breakeven-daily-revenue">
                  {formatCurrency(calculations.dailyRevenue)}
                </p>
              </div>
              <div className="border-t border-white/10 pt-3">
                <p className="text-xs text-white/50 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>Orders Per Month</p>
                <p className="text-2xl font-bold" style={{ fontFamily: "'Bebas Neue', cursive" }} data-testid="breakeven-orders-month">
                  {Math.ceil(calculations.ordersPerMonth)}
                </p>
              </div>
              <div>
                <p className="text-xs text-white/50 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>Orders Per Day</p>
                <p className="text-2xl font-bold" style={{ fontFamily: "'Bebas Neue', cursive" }} data-testid="breakeven-orders-day">
                  {Math.ceil(calculations.ordersPerDay)}
                </p>
              </div>
            </div>
          </div>

          {/* Visual indicator */}
          <div className="pizza-card p-6 text-center">
            <TrendingUp className={`w-12 h-12 mx-auto mb-2 ${calculations.breakEvenRevenue > 0 ? 'text-[#2E7D32]' : 'text-[#2A2A2A]/20'}`} />
            <p className="text-sm text-[#2A2A2A]/60">
              {calculations.breakEvenRevenue > 0
                ? `You need ${Math.ceil(calculations.ordersPerDay)} orders/day at ${formatCurrency(Number(breakEvenData.avgOrderValue))} average to break even.`
                : 'Enter your fixed costs to see your break-even point.'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
